import React, { useState } from 'react';
import { Alert, Button, Form, Input, Popconfirm, Space, Typography, message } from 'antd';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';

const { Paragraph, Text } = Typography;

/** What the platform is actually using for a feed key (server-reported `source`): stored | env | none. */
export function platformKeyStatus(c) {
    if (!c || c.source === 'none') return 'none';
    if (c.storedUnreadable) return 'attention';
    return 'connected';
}

/**
 * Platform-wide feed key (`/admin/platform-credentials*`, platform_admin only; not tenant-scoped).
 * Replace-only: the server returns a masked preview, never the key.
 */
const PlatformKeyPanel = ({ provider, credential, onSave, onRemove }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);

    const save = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            await onSave({ secretValue: values.secretValue.trim(), displayLabel: values.displayLabel?.trim() || undefined });
            form.resetFields();
        } catch (err) {
            message.error(err.message || 'Could not save the key.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setRemoving(true);
        try {
            await onRemove();
        } catch (err) {
            message.error(err.message || 'Could not remove the key.');
        } finally {
            setRemoving(false);
        }
    };

    return (
        <div>
            {provider.description ? <Paragraph type="secondary">{provider.description}</Paragraph> : null}
            {credential?.source === 'env' && !credential?.id && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }} message="An environment key is in use. Storing a key here overrides it." />
            )}
            {credential?.storedUnreadable && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={
                        credential.source === 'env'
                            ? 'Stored key unreadable - the environment fallback is in use.'
                            : 'Stored key unreadable - no key is in use.'
                    }
                    description="Enter a new key below to replace the unreadable stored one."
                />
            )}
            {credential?.id && (
                <Paragraph>
                    <Text strong>Stored key: </Text>
                    <Text code>{credential.maskedPreview || 'hidden'}</Text>
                    <br />
                    <Text type="secondary">
                        {credential.displayLabel ? `${credential.displayLabel} · ` : ''}
                        Last rotated {formatDdMmYyyy(credential.lastRotatedAt || credential.updatedAt)}
                    </Text>
                </Paragraph>
            )}
            <Form form={form} layout="vertical" autoComplete="off" requiredMark={false}>
                <Form.Item name="displayLabel" label="Label (optional)">
                    <Input maxLength={100} autoComplete="off" />
                </Form.Item>
                <Form.Item
                    name="secretValue"
                    label={credential?.id ? 'New API key (replaces stored key)' : 'API key'}
                    rules={[{ required: true, whitespace: true, message: 'API key is required.' }]}
                >
                    <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Space>
                    <Button type="primary" onClick={save} icon={<SaveOutlined />} loading={saving}>
                        {credential?.id ? 'Rotate key' : 'Save key'}
                    </Button>
                    {credential?.id && (
                        <Popconfirm
                            title={`Disconnect ${provider.label}?`}
                            description="The feed will stop authenticating until a new key is entered."
                            okText="Disconnect"
                            okButtonProps={{ danger: true }}
                            onConfirm={remove}
                        >
                            <Button danger icon={<DeleteOutlined />} loading={removing}>Disconnect</Button>
                        </Popconfirm>
                    )}
                </Space>
            </Form>
        </div>
    );
};

export default PlatformKeyPanel;
